import React, { FunctionComponent, useState, useEffect, useCallback } from "react"
import { ApolloClient, ApolloError, useApolloClient, useMutation } from "@apollo/client"
import { AudioFile } from "../../../typings/types"
import { APPROVE_AUDIO } from "../../mutations/APPROVE_AUDIO"
import {
    Select,
    Grid,
    Button,
    Typography,
    InputLabel,
    CircularProgress,
    Modal,
    Fade,
    FormControl,
    MenuItem
} from "@material-ui/core"
import { Fields, updateSelect, setAudio, AudioData, reverseStepper } from "./reducer"
import { useManualPageContext } from "./useManualPageContext"
import { DownloadHelpers, File } from "../../common/DownloadHelpers"
import manualPageStyles from "./ManualPage.module.scss"
import { TransactionMode } from "../../common/enums"
import styles from "./DownloadAudio.module.scss"
import { useNotification } from "../../common/hooks/useNotification"
import { FetchResult } from "apollo-link"

enum DownloadStatus {
    NONE,
    LOADING,
    DOWNLOADED
}

export const DownloadAudio: FunctionComponent = () => {
    const { sourceAudio, targetAudio, state, dispatch, resetManualPage } = useManualPageContext()
    const { audioFormatOptions, fields, sourceLanguage, targetLanguage } = state
    const [sourceFileName, setSourceFileName] = useState<string>("")
    const [targetFileName, setTargetFileName] = useState<string>("")
    const [sourceAudioDownloadStatus, setSourceAudioDownloadStatus] = useState(DownloadStatus.NONE)
    const [targetAudioDownloadStatus, setTargetAudioDownloadStatus] = useState(DownloadStatus.NONE)
    const [successModal, setSuccessModal] = useState(false)
    const { notifySuccess, notifyError } = useNotification()
    const client = useApolloClient()

    const handleDownload = async () => {
        setSuccessModal(true)
        let sourceAudioApprovalQuery
        let targetAudioApprovalQuery
        const files: File[] = []

        if (sourceAudio?.data?.createAudio) {
            setSourceAudioDownloadStatus(DownloadStatus.LOADING)
            sourceAudioApprovalQuery = client.mutate<{ approveAudio: AudioFile }>({
                variables: {
                    approveAudioInput: {
                        referenceId: state.referenceId,
                        mode: TransactionMode.SINGLE,
                        formatId: parseInt(fields.fileFormat.value),
                        languageId: sourceLanguage,
                        givenText: fields.originalText.value,
                        voiceId: parseInt(fields.sourceVoice.value),
                        audioFile: {
                            binaryData: sourceAudio?.data?.createAudio.binaryData,
                            size: sourceAudio?.data?.createAudio.binaryData.length,
                            fileName: fields.audioFileName.value
                        }
                    }
                },
                mutation: APPROVE_AUDIO
            })
        }

        if (targetAudio?.data?.createAudio) {
            setTargetAudioDownloadStatus(DownloadStatus.LOADING)
            targetAudioApprovalQuery = client.mutate<{ approveAudio: AudioFile }>({
                variables: {
                    approveAudioInput: {
                        referenceId: state.referenceId,
                        mode: TransactionMode.SINGLE,
                        formatId: parseInt(fields.fileFormat.value),
                        languageId: targetLanguage,
                        givenText: fields.translatedText.value,
                        voiceId: parseInt(fields.targetVoice.value),
                        audioFile: {
                            binaryData: targetAudio?.data?.createAudio.binaryData,
                            size: targetAudio?.data?.createAudio.binaryData.length,
                            fileName: fields.audioFileName.value
                        }
                    }
                },
                mutation: APPROVE_AUDIO
            })
        }

        if (sourceAudioApprovalQuery)
            await sourceAudioApprovalQuery
                .then(({ data }) => {
                    if (data?.approveAudio) {
                        notifySuccess("Original audio has been approved")
                        dispatch(setAudio(AudioData.ApprovedSourceAudio, data.approveAudio))
                        setSourceAudioDownloadStatus(DownloadStatus.DOWNLOADED)
                        files.push({
                            folderName: state.allLanguages[state.sourceLanguage].name,
                            fileName: data.approveAudio.fileName,
                            binaryData: data.approveAudio.binaryData
                        })
                    }
                })
                .catch((err: ApolloError) => {
                    notifyError(
                        `There was an error with approving the original audio: ${err.message}`
                    )
                    setSourceAudioDownloadStatus(DownloadStatus.NONE)
                })

        if (targetAudioApprovalQuery)
            await targetAudioApprovalQuery
                .then(({ data }) => {
                    if (data?.approveAudio) {
                        notifySuccess("Translated audio has been approved")
                        dispatch(setAudio(AudioData.ApprovedTargetAudio, data.approveAudio))
                        setTargetAudioDownloadStatus(DownloadStatus.DOWNLOADED)
                        files.push({
                            folderName: state.allLanguages[state.targetLanguage].name,
                            fileName: data.approveAudio.fileName,
                            binaryData: data.approveAudio.binaryData
                        })
                    }
                })
                .catch((err: ApolloError) => {
                    notifyError(
                        `There was an error with approving the original audio: ${err.message}`
                    )
                    setSourceAudioDownloadStatus(DownloadStatus.NONE)
                })

        DownloadHelpers.zipAndSave(files)
    }

    const constructFileName = useCallback(
        (baseFileName: string | undefined) => {
            if (!baseFileName) return ""
            const fileFormatId = parseInt(fields.fileFormat.value)
            const ext = audioFormatOptions[fileFormatId]
                ? audioFormatOptions[fileFormatId].type
                : ""
            return `${baseFileName}.${ext}`
        },
        [fields.fileFormat, audioFormatOptions]
    )

    useEffect(() => {
        setSourceFileName(constructFileName(sourceAudio?.data?.createAudio.fileName))
        setTargetFileName(constructFileName(targetAudio?.data?.createAudio.fileName))
    }, [sourceAudio, targetAudio, constructFileName])

    const translationOccurred = () => {
        return sourceAudio?.data && targetAudio?.data
    }

    const noTranslationOccurred = () => {
        return sourceAudio?.data && !targetAudio?.data
    }

    useEffect(() => {
        if (translationOccurred()) {
            if (
                sourceAudioDownloadStatus === DownloadStatus.DOWNLOADED &&
                targetAudioDownloadStatus === DownloadStatus.DOWNLOADED
            ) {
                setSourceAudioDownloadStatus(DownloadStatus.NONE)
                setTargetAudioDownloadStatus(DownloadStatus.NONE)
            }
        }
        if (noTranslationOccurred()) {
            if (sourceAudioDownloadStatus === DownloadStatus.DOWNLOADED) {
                setSourceAudioDownloadStatus(DownloadStatus.NONE)
            }
        }
    }, [sourceAudioDownloadStatus, targetAudioDownloadStatus, resetManualPage])

    const approvalsAreLoading = () => {
        if (translationOccurred())
            return (
                sourceAudioDownloadStatus === DownloadStatus.LOADING ||
                targetAudioDownloadStatus === DownloadStatus.LOADING
            )
        if (noTranslationOccurred()) return sourceAudioDownloadStatus === DownloadStatus.LOADING
    }

    return (
        <Grid container alignItems="stretch" spacing={3}>
            <Grid item className={manualPageStyles.Step__description}>
                <Typography variant="caption">
                    Select the audio formats you'd like generated for each audio track. Click SAVE
                    to begin the audio generation process. Your audio files will automatically begin
                    downloading when they're ready.
                </Typography>
            </Grid>
            <Grid item className={manualPageStyles.Step__content}>
                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <FormControl variant="outlined" id="select-file-format-control">
                            <InputLabel
                                id="select-file-format-label"
                                data-testid="select-file-format-label"
                                htmlFor={Fields.FileFormat}
                            >
                                File Format
                            </InputLabel>
                            {!!Object.keys(audioFormatOptions).length && (
                                <Select
                                    labelId="select-file-format-label"
                                    label="File Format"
                                    id={Fields.FileFormat}
                                    inputProps={{ "data-testid": "fileFormatSelector" }}
                                    onChange={(e: any) => {
                                        setSourceFileName(
                                            constructFileName(
                                                sourceAudio?.data?.createAudio.fileName
                                            )
                                        )
                                        setTargetFileName(
                                            constructFileName(
                                                targetAudio?.data?.createAudio.fileName
                                            )
                                        )
                                        dispatch(updateSelect(Fields.FileFormat, e.target.value))
                                    }}
                                    value={fields.fileFormat.value}
                                >
                                    {Object.values(audioFormatOptions).map(format => {
                                        return (
                                            <MenuItem key={format.id} value={format.id.toString()}>
                                                {format.name}
                                            </MenuItem>
                                        )
                                    })}
                                </Select>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item>
                        {sourceAudio?.data?.createAudio && (
                            <div>
                                <Typography
                                    variant="body1"
                                    gutterBottom
                                    data-testid="sourceFileName"
                                >
                                    {sourceFileName}
                                </Typography>
                            </div>
                        )}
                    </Grid>
                    <Grid item>
                        {targetAudio?.data?.createAudio && (
                            <div>
                                <Typography
                                    variant="body1"
                                    gutterBottom
                                    data-testid="targetFileName"
                                >
                                    {targetFileName}
                                </Typography>
                            </div>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <Grid item className={manualPageStyles.Step__content}>
                <Button
                    data-testid="downloadAudioBack"
                    id="downloadAudioBack"
                    onClick={() => {
                        dispatch(reverseStepper())
                    }}
                >
                    Back
                </Button>
                <Button
                    variant="contained"
                    disabled={approvalsAreLoading()}
                    data-testid="downloadFilesBtn"
                    id="downloadFilesBtn"
                    color="primary"
                    onClick={handleDownload}
                >
                    {approvalsAreLoading() && <CircularProgress size={24} color={"secondary"} />}
                    {!approvalsAreLoading() && "Save"}
                </Button>
                <Modal
                    open={successModal}
                    onClose={() => setSuccessModal(false)}
                    closeAfterTransition
                    disableBackdropClick
                    id={"modal"}
                    className={styles.DownloadAudio__modal}
                >
                    <Fade in={successModal}>
                        <div className={styles.DownloadAudio__modalcard}>
                            <div className={styles.DownloadAudio__contents}>
                                <Typography className={styles.DownloadAudio__desc}>
                                    You've approved your new audio and your files are now being
                                    generated. When they are done, they will begin downloading
                                    automatically.
                                </Typography>
                                <Button
                                    variant="contained"
                                    id="closeModalBtn"
                                    color="primary"
                                    className={styles.DownloadAudio__donebtn}
                                    disabled={approvalsAreLoading()}
                                    onClick={() => {
                                        setSuccessModal(false)
                                        resetManualPage()
                                    }}
                                >
                                    {approvalsAreLoading() && (
                                        <CircularProgress size={24} color={"secondary"} />
                                    )}
                                    {!approvalsAreLoading() && `Done`}
                                </Button>
                            </div>
                        </div>
                    </Fade>
                </Modal>
            </Grid>
        </Grid>
    )
}
